import { useMemo, useRef, useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Pagination } from "antd";
import {
  Calendar,
  Check,
  ChevronDown,
  Copy,
  Download,
  Edit2,
  Info,
  Link2,
  Paperclip,
  Trash,
  X,
} from "lucide-react";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { getEmployeeId } from "@/lib/storeManager";
import {
  PtsChangeRequestService,
  PtsProjectService,
  type ChangeRequestAttachment,
  type ChangeRequestItem,
  type ChangeRequestPriority,
} from "@/services/APIs";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { isPTSAdmin } from "../../utils/rolePermissions";
import { ChangeRequestStatusBadge } from "./StatusBadge";
import ChangeRequestFilters from "./ChangeRequestFilters";
import {
  EMPTY_CHANGE_REQUEST_FILTERS,
  type ChangeRequestFilterValues,
} from "./ChangeRequestFilters.constants";
import {
  CHANGE_REQUEST_PRIORITIES,
  filterChangeRequests,
  formatDateYmd,
  formatHours,
  getPlainTextFromHtml,
  resolveName,
} from "./utils";

const changeRequestQuillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
};

type EmployeeOption = { value: string; label: string };

type FormState = {
  project: string;
  title: string;
  priority: ChangeRequestPriority;
  requested_date: string;
  expected_completion_date: string;
  assign_employees: string[];
  estimated_hours: string;
  description: string;
  raised_to_client_status: string;
  raised_date: string;
  google_sheet_link: string;
};

const emptyForm = (): FormState => ({
  project: "",
  title: "",
  priority: "Medium",
  requested_date: "",
  expected_completion_date: "",
  assign_employees: [],
  estimated_hours: "",
  description: "",
  raised_to_client_status: "Pending",
  raised_date: "",
  google_sheet_link: "",
});

function toDateInputValue(value?: string | null): string {
  if (!value) {
    return "";
  }
  const ymd = formatDateYmd(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : "";
}

function toEmployeeIds(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(item => String(item)).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
}

type BusinessAnalystTabProps = {
  projectNameById: Map<string, string>;
  employeeNameById: Map<string, string>;
  employeeOptions: EmployeeOption[];
};

const BusinessAnalystTab = ({
  projectNameById,
  employeeNameById,
  employeeOptions,
}: BusinessAnalystTabProps) => {
  const queryClient = useQueryClient();
  const formCardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const quillEditorRef = useRef<ReactQuill>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<
    ChangeRequestAttachment[]
  >([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [employeeOpen, setEmployeeOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [isGoogleSheetModalOpen, setIsGoogleSheetModalOpen] = useState(false);
  const [googleSheetInput, setGoogleSheetInput] = useState("");
  const [filterValues, setFilterValues] = useState<ChangeRequestFilterValues>(
    EMPTY_CHANGE_REQUEST_FILTERS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues]);

  const isEditMode = !!editingId;
  const canImportGoogleSheet = isPTSAdmin();
  const currentEmployeeId = getEmployeeId() ?? "";
  const canViewAllBaRequests = isPTSAdmin();
  const baFilter = canViewAllBaRequests
    ? undefined
    : currentEmployeeId || undefined;

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["change-request-open-projects"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "change-requests",
      "ba-list",
      canViewAllBaRequests ? "all" : currentEmployeeId,
      filterValues.projectId,
      filterValues.status,
      filterValues.raisedToClientStatus,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      PtsChangeRequestService.getChangeRequests({
        page: currentPage,
        page_size: pageSize,
        project: filterValues.projectId,
        status: filterValues.status,
        raised_to_client_status: filterValues.raisedToClientStatus,
        ba: baFilter,
      }),
    enabled: canViewAllBaRequests || !!currentEmployeeId,
  });

  const recentRequests = data?.results;
  const totalCount = data?.count ?? 0;

  const filteredRequests = useMemo(
    () =>
      filterChangeRequests(recentRequests ?? [], {
        projectId: filterValues.projectId,
        status: filterValues.status,
        raisedToClientStatus: filterValues.raisedToClientStatus,
      }),
    [recentRequests, filterValues]
  );

  const { data: googleSheetLinkData } = useQuery({
    queryKey: ["change-request", "google-sheet-link"],
    queryFn: () => PtsChangeRequestService.getGoogleSheetLink(),
  });

  const savedGoogleSheetLink = String(
    googleSheetLinkData?.google_sheet_link ?? ""
  ).trim();

  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyGoogleSheetLink = async () => {
    if (!savedGoogleSheetLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(savedGoogleSheetLink);
      notify.success("Link copied to clipboard");
      setCopied(true);
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000) as unknown as number;
    } catch {
      notify.error("Unable to copy link");
      setCopied(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await PtsChangeRequestService.getChangeRequests({
        page_size: 100000,
        project: filterValues.projectId,
        status: filterValues.status,
        raised_to_client_status: filterValues.raisedToClientStatus,
        ba: baFilter,
      });

      const items = res.results;

      const escapeCsv = (val: any) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "CR Number",
        "Project Name",
        "Title",
        "Priority",
        "Requested Date",
        "Expected Completion Date",
        "Assign Employees",
        "Estimated Hours",
        "Description",
        "Status",
        "BA Name",
        "Cost",
        "Billing Configured",
        "Invoice Raised Status",
        "Raised to Client Status",
        "Raised Date",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const projName =
          item.project_name ?? resolveName(item.project, projectNameById);
        const baName = item.ba_name ?? resolveName(item.ba, employeeNameById);

        const rawEmployees = item.assign_employees;
        const employeesList = Array.isArray(rawEmployees)
          ? rawEmployees
              .map((id: string) => employeeNameById.get(id) ?? id)
              .join(", ")
          : typeof rawEmployees === "string"
            ? (rawEmployees as string)
                .split(",")
                .map(
                  (id: string) => employeeNameById.get(id.trim()) ?? id.trim()
                )
                .join(", ")
            : "";

        return [
          escapeCsv(item.cr_number),
          escapeCsv(projName),
          escapeCsv(item.title),
          escapeCsv(item.priority),
          escapeCsv(formatDateYmd(item.requested_date)),
          escapeCsv(formatDateYmd(item.expected_completion_date)),
          escapeCsv(employeesList),
          escapeCsv(item.estimated_hours),
          escapeCsv(item.description),
          escapeCsv(item.status),
          escapeCsv(baName),
          escapeCsv(item.cost),
          escapeCsv(item.billing_configured),
          escapeCsv(item.invoice_raised_status),
          escapeCsv(item.raised_to_client_status),
          escapeCsv(formatDateYmd(item.raised_date)),
          escapeCsv(formatDateYmd(item.created_on)),
          escapeCsv(formatDateYmd(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `change-requests-ba-${formatDateYmd(new Date().toISOString())}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
    setAttachments([]);
    setExistingAttachments([]);
    setEditingId(null);
    setEmployeeSearch("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const saveMutation = useMutation<
    ChangeRequestItem | undefined,
    Error,
    { keepForm?: boolean } | undefined
  >({
    mutationFn: async (_variables: { keepForm?: boolean } | undefined) => {
      const payload = {
        ...form,
        google_sheet_link:
          form.google_sheet_link?.trim() || savedGoogleSheetLink,
        attachments,
      };
      if (editingId) {
        return PtsChangeRequestService.updateChangeRequestForm(
          editingId,
          payload
        );
      }
      return PtsChangeRequestService.createChangeRequest(payload);
    },
    onSuccess: (result, variables) => {
      const keepForm = variables?.keepForm ?? false;
      if (!result?.id) {
        return;
      }
      notify.success(
        keepForm
          ? "Change request copied and saved successfully"
          : isEditMode
            ? "Change request updated successfully"
            : "Change request submitted for approval"
      );
      if (keepForm) {
        setEditingId(null);
        setExistingAttachments([]);
        setAttachments([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        resetForm();
      }
      queryClient.invalidateQueries({ queryKey: ["change-requests"] });
    },
  });

  const googleSheetMutation = useMutation({
    mutationFn: (url: string) =>
      PtsChangeRequestService.importGoogleSheet({ google_sheet_link: url }),
    onSuccess: () => {
      setIsGoogleSheetModalOpen(false);
      setGoogleSheetInput("");
      notify.success("Google sheet link imported successfully");
      queryClient.invalidateQueries({
        queryKey: ["change-request", "google-sheet-link"],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => PtsChangeRequestService.deleteChangeRequest(id),
    onSuccess: (_result, id) => {
      notify.success("Change request deleted successfully");
      if (editingId === id) {
        resetForm();
      }
      queryClient.invalidateQueries({ queryKey: ["change-requests"] });
    },
  });

  const filteredEmployees = useMemo(() => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) {
      return employeeOptions;
    }
    return employeeOptions.filter(emp => emp.label.toLowerCase().includes(q));
  }, [employeeOptions, employeeSearch]);

  const selectedEmployeeLabels = useMemo(() => {
    return form.assign_employees
      .map(id => employeeNameById.get(id) ?? id)
      .filter(Boolean);
  }, [form.assign_employees, employeeNameById]);

  const toggleEmployee = (id: string) => {
    setForm(prev => {
      const exists = prev.assign_employees.includes(id);
      return {
        ...prev,
        assign_employees: exists
          ? prev.assign_employees.filter(item => item !== id)
          : [...prev.assign_employees, id],
      };
    });
  };

  const onFilesSelected = (files: FileList | null) => {
    if (!files?.length) {
      return;
    }
    setAttachments(prev => [...prev, ...Array.from(files)]);
  };

  const validate = (): string | null => {
    if (!form.project) {
      return "Please select a project";
    }
    if (!form.title.trim()) {
      return "Please enter a change request title";
    }
    if (!form.priority) {
      return "Please select a priority";
    }
    if (!form.requested_date) {
      return "Please select a requested date";
    }
    if (!form.expected_completion_date) {
      return "Please select an expected completion date";
    }
    if (form.assign_employees.length === 0) {
      return "Please assign at least one employee";
    }
    if (!form.estimated_hours || Number(form.estimated_hours) <= 0) {
      return "Please enter estimated hours";
    }
    if (!getPlainTextFromHtml(form.description)) {
      return "Please enter a description";
    }
    return null;
  };

  const handleSubmit = () => {
    const error = validate();
    if (error) {
      notify.error(error);
      return;
    }
    saveMutation.mutate({ keepForm: false });
  };

  const handleCopyToCreate = () => {
    const error = validate();
    if (error) {
      notify.error(error);
      return;
    }
    saveMutation.mutate({ keepForm: true });
  };

  const handleEdit = (item: ChangeRequestItem) => {
    const priority = (["High", "Medium", "Low"] as const).includes(
      item.priority as ChangeRequestPriority
    )
      ? (item.priority as ChangeRequestPriority)
      : "Medium";

    setEditingId(item.id);
    setForm({
      project: String(item.project || ""),
      title: item.title || "",
      priority,
      requested_date: toDateInputValue(item.requested_date),
      expected_completion_date: toDateInputValue(item.expected_completion_date),
      assign_employees: toEmployeeIds(item.assign_employees),
      estimated_hours:
        item.estimated_hours === null || item.estimated_hours === undefined
          ? ""
          : String(item.estimated_hours),
      description: item.description ?? "",
      raised_to_client_status: item.raised_to_client_status ?? "",
      raised_date: toDateInputValue(item.raised_date),
      google_sheet_link: item.google_sheet_link ?? "",
    });
    setAttachments([]);
    setExistingAttachments(item.attachments ?? []);
    setGoogleSheetInput(item.google_sheet_link ?? "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <style>{`
        .change-request-quill-editor {
          width: 100% !important;
        }

        .change-request-quill-editor .ql-container {
          height: auto !important;
          overflow: hidden !important;
          border-bottom-left-radius: 0.5rem !important;
          border-bottom-right-radius: 0.5rem !important;
          border: 1px solid #e5e7eb !important;
          font-size: 14px !important;
          background-color: #fff !important;
        }

        .change-request-quill-editor .ql-editor {
          min-height: 120px !important;
          max-height: 240px !important;
          height: auto !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          padding: 12px !important;
          line-height: 1.6 !important;
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
        }

        .change-request-quill-editor .ql-toolbar {
          border-top-left-radius: 0.5rem !important;
          border-top-right-radius: 0.5rem !important;
          border: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          background-color: #fafafa !important;
        }
      `}</style>
      <div className="space-y-6">
        <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
          <h1 className="shrink-0 text-xl font-bold text-[#111827] md:text-2xl">
            Change requests
          </h1>
          <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            {savedGoogleSheetLink ? (
              <div className="flex w-full min-w-0 items-center sm:w-auto sm:max-w-[min(100%,28rem)] sm:justify-end">
                <div className="flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-gray-300 bg-slate-50 px-3 py-[0.35rem] text-sm text-[#111827]">
                  <a
                    href={savedGoogleSheetLink}
                    target="_blank"
                    rel="noreferrer"
                    className="block min-w-0 flex-1 truncate text-blue-600 underline"
                  >
                    {savedGoogleSheetLink}
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyGoogleSheetLink}
                    aria-label={copied ? "Copied" : "Copy link"}
                    className="shrink-0 rounded border border-transparent p-1 hover:border-gray-200 hover:bg-slate-100"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            ) : null}
            {canImportGoogleSheet ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setGoogleSheetInput(savedGoogleSheetLink);
                  setIsGoogleSheetModalOpen(true);
                }}
                disabled={
                  saveMutation.isPending || googleSheetMutation.isPending
                }
                className="h-10 flex-1 px-5 sm:flex-none"
              >
                <Link2 className="mr-2 h-4 w-4" />
                Import
              </Button>
            ) : null}
            <Button
              type="button"
              variant="default"
              onClick={handleExportCSV}
              className="h-10 flex-1 px-5 sm:flex-none"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        <Card ref={formCardRef} className="border-gray-200 bg-white shadow-sm">
          <CardContent className="space-y-5 p-5 md:p-6">
            <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-[#111827]">
                  {isEditMode ? "Edit change request" : "Raise change request"}
                </h2>
                <Info className="h-4 w-4 text-gray-400" />
              </div>
              <div className="flex flex-col gap-3 sm:items-end">
                <div className="flex flex-wrap items-center gap-2">
                  {isEditMode ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                      disabled={saveMutation.isPending}
                      className="h-10 px-5"
                    >
                      Cancel
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopyToCreate}
                    disabled={saveMutation.isPending}
                    className="h-10 px-5"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy to Create
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={saveMutation.isPending}
                    className="bg-primary hover:bg-primary/90 h-10 px-5 text-white"
                  >
                    {saveMutation.isPending
                      ? isEditMode
                        ? "Updating..."
                        : "Submitting..."
                      : isEditMode
                        ? "Update"
                        : "Submit for approval"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="space-y-1.5 md:col-span-6">
                <Label className="text-sm text-[#374151]">
                  Project <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.project || undefined}
                  onValueChange={value =>
                    setForm(prev => ({ ...prev, project: value }))
                  }
                >
                  <SelectTrigger className="h-10 bg-white">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {projectOptions.map(project => (
                      <SelectItem key={project.value} value={project.value}>
                        {project.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 md:col-span-6">
                <Label className="text-sm text-[#374151]">
                  Change request title <span className="text-red-500">*</span>
                </Label>
                <Input
                  className="h-10 bg-white"
                  placeholder="e.g. Add QR Code in Certificate"
                  value={form.title}
                  onChange={e =>
                    setForm(prev => ({ ...prev, title: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-1.5 md:col-span-6 lg:col-span-3">
                <Label className="text-sm text-[#374151]">
                  Priority <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.priority}
                  onValueChange={value =>
                    setForm(prev => ({
                      ...prev,
                      priority: value as ChangeRequestPriority,
                    }))
                  }
                >
                  <SelectTrigger className="h-10 bg-white">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {CHANGE_REQUEST_PRIORITIES.map(priority => (
                      <SelectItem key={priority} value={priority}>
                        {priority}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 md:col-span-6 lg:col-span-3">
                <Label className="text-sm text-[#374151]">
                  Requested date <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    type="date"
                    className="h-10 bg-white pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                    value={form.requested_date}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        requested_date: e.target.value,
                      }))
                    }
                  />
                  <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="space-y-1.5 md:col-span-6 lg:col-span-3">
                <Label className="text-sm text-[#374151]">
                  Expected completion <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    type="date"
                    className="h-10 bg-white pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:h-4 [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                    value={form.expected_completion_date}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        expected_completion_date: e.target.value,
                      }))
                    }
                  />
                  <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="space-y-1.5 md:col-span-6 lg:col-span-3">
                <Label className="text-sm text-[#374151]">
                  Assign employees <span className="text-red-500">*</span>
                </Label>
                <Popover open={employeeOpen} onOpenChange={setEmployeeOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="border-input flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 text-left text-sm"
                    >
                      <span
                        className={
                          selectedEmployeeLabels.length
                            ? "truncate text-[#111827]"
                            : "text-muted-foreground"
                        }
                      >
                        {selectedEmployeeLabels.length
                          ? selectedEmployeeLabels.join(", ")
                          : "Select employees"}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-[var(--radix-popover-trigger-width)] bg-white p-0"
                  >
                    <div className="border-b p-2">
                      <Input
                        placeholder="Search employees..."
                        value={employeeSearch}
                        onChange={e => setEmployeeSearch(e.target.value)}
                        className="h-9 bg-white"
                      />
                    </div>
                    <div className="max-h-56 overflow-y-auto p-2">
                      {filteredEmployees.length === 0 ? (
                        <p className="px-2 py-4 text-center text-sm text-gray-500">
                          No employees found
                        </p>
                      ) : (
                        filteredEmployees.map(emp => {
                          const checked = form.assign_employees.includes(
                            emp.value
                          );
                          return (
                            <label
                              key={emp.value}
                              className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() =>
                                  toggleEmployee(emp.value)
                                }
                              />
                              <span className="flex-1">{emp.label}</span>
                              {checked ? (
                                <Check className="h-3.5 w-3.5 text-emerald-600" />
                              ) : null}
                            </label>
                          );
                        })
                      )}
                    </div>
                    {form.assign_employees.length > 0 ? (
                      <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-gray-500">
                        <span>{form.assign_employees.length} selected</span>
                        <button
                          type="button"
                          className="text-primary font-medium"
                          onClick={() =>
                            setForm(prev => ({ ...prev, assign_employees: [] }))
                          }
                        >
                          Clear
                        </button>
                      </div>
                    ) : null}
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1.5 md:col-span-6">
                <Label className="text-sm text-[#374151]">
                  Estimated hours <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  min={0}
                  step="0.5"
                  className="h-10 bg-white"
                  placeholder="e.g. 18"
                  value={form.estimated_hours}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      estimated_hours: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-1.5 md:col-span-3">
                <Label className="text-sm text-[#374151]">
                  Raised to client status
                </Label>
                <Select
                  value={form.raised_to_client_status || undefined}
                  onValueChange={value =>
                    setForm(prev => ({
                      ...prev,
                      raised_to_client_status: value,
                    }))
                  }
                >
                  <SelectTrigger className="h-10 bg-white">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {["Pending", "On Hold", "Reject", "Approved"].map(
                      status => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 md:col-span-3">
                <Label className="text-sm text-[#374151]">Raised date</Label>
                <div className="relative">
                  <Input
                    type="date"
                    className="h-10 bg-white pr-10"
                    value={form.raised_date}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        raised_date: e.target.value,
                      }))
                    }
                  />
                  <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="space-y-1.5 md:col-span-6">
                <Label className="text-sm text-[#374151]">
                  Description <span className="text-red-500">*</span>
                </Label>
                <ReactQuill
                  key={editingId ?? "new-change-request"}
                  ref={quillEditorRef}
                  theme="snow"
                  value={form.description}
                  onChange={value =>
                    setForm(prev => ({ ...prev, description: value }))
                  }
                  className="change-request-quill-editor"
                  modules={changeRequestQuillModules}
                  placeholder="Describe what the client asked for..."
                />
              </div>

              <div className="space-y-1.5 md:col-span-6">
                <Label className="text-sm text-[#374151]">
                  Attachments{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </Label>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex min-h-[100px] w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-14 text-sm text-gray-500 transition hover:border-gray-400 hover:bg-gray-50"
                >
                  <Paperclip className="h-5 w-5 text-gray-400" />
                  <span>Upload email, PDF or screenshot.</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.eml,.msg"
                  className="hidden"
                  onChange={e => onFilesSelected(e.target.files)}
                />
                {existingAttachments.length > 0 ? (
                  <ul className="mt-2 space-y-2">
                    {existingAttachments.map(file => (
                      <li
                        key={`${file.document_id}`}
                        className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                      >
                        <span className="truncate text-gray-600">
                          {file.name ?? "Existing attachment"}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {attachments.length > 0 ? (
                  <ul className="mt-2 space-y-2">
                    {attachments.map((file, index) => (
                      <li
                        key={`${file.name}`}
                        className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                      >
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          className="ml-2 text-gray-400 hover:text-rose-500"
                          onClick={() =>
                            setAttachments(prev =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <Dialog
          open={isGoogleSheetModalOpen}
          onOpenChange={setIsGoogleSheetModalOpen}
        >
          <DialogContent className="bg-white sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Import Google Sheet Link</DialogTitle>
              <DialogDescription>
                Paste the Google Sheet URL and import it for this change
                request.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Google sheet link</Label>
              <Input
                className="bg-white"
                placeholder="https://..."
                value={googleSheetInput}
                onChange={e => setGoogleSheetInput(e.target.value)}
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsGoogleSheetModalOpen(false)}
                disabled={googleSheetMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const trimmed = googleSheetInput.trim();
                  if (!trimmed) {
                    notify.error("Please enter a Google Sheet link");
                    return;
                  }
                  googleSheetMutation.mutate(trimmed);
                }}
                disabled={googleSheetMutation.isPending}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                {googleSheetMutation.isPending ? "Importing..." : "Import"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ChangeRequestFilters
          values={filterValues}
          onChange={setFilterValues}
          idPrefix="ba-change-request"
        />

        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-5 py-3 font-medium">CR Number</th>
                    <th className="px-5 py-3 font-medium">Project</th>
                    <th className="px-5 py-3 font-medium">Title</th>
                    <th className="px-5 py-3 font-medium">Estimated Hours</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Requested Date</th>
                    <th className="px-5 py-3 font-medium">
                      Raised to Client Status
                    </th>
                    <th className="px-5 py-3 font-medium">Raised Date</th>
                    <th className="px-5 py-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        Loading change requests...
                      </td>
                    </tr>
                  ) : filteredRequests.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        No change requests yet.
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map(item => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-50 last:border-0"
                      >
                        <td className="px-5 py-3.5 font-medium text-[#111827]">
                          {item.cr_number || "—"}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {item.project_name ??
                            resolveName(item.project, projectNameById)}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {item.title || "—"}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatHours(item.estimated_hours)}
                        </td>
                        <td className="px-5 py-3.5">
                          <ChangeRequestStatusBadge status={item.status} />
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatDateYmd(item.requested_date)}
                        </td>
                        <td className="px-5 py-3.5">
                          {item.raised_to_client_status ? (
                            <ChangeRequestStatusBadge
                              status={item.raised_to_client_status}
                            />
                          ) : (
                            <span className="text-[#374151]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatDateYmd(item.raised_date)}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-primary h-8 w-8 hover:text-blue-800"
                              title="Edit"
                              onClick={() => handleEdit(item)}
                              disabled={item.status === "Reject"}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Popconfirm
                              title="Delete change request?"
                              description="This action cannot be undone."
                              okText="Delete"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => deleteMutation.mutate(item.id)}
                              disabled={
                                item.status === "Reject" ||
                                item.status === "Approved"
                              }
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-rose-400"
                                title="Delete"
                                disabled={
                                  item.status === "Reject" ||
                                  item.status === "Approved" ||
                                  (deleteMutation.isPending &&
                                    deleteMutation.variables === item.id)
                                }
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </Popconfirm>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalCount > 0 && (
              <div className="flex items-center justify-end border-t p-4">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalCount}
                  onChange={(page, size) => {
                    if (size && size !== pageSize) {
                      setPageSize(size);
                      setCurrentPage(1);
                      return;
                    }
                    setCurrentPage(page);
                  }}
                  onShowSizeChange={(_current, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  pageSizeOptions={["50", "100", "200", "500"]}
                  defaultPageSize={100}
                  disabled={isLoading}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default BusinessAnalystTab;
